import Vue from "vue";
import { Url } from "@/commons/constants/Url";
import SendSystemErrorService from "@/commons/services/SendSystemError";
import { commonStore, userStore } from "@/store/store";
import SystemError, { HitomeqErrorType } from "./commons/models/SystemError";

/**
 * ## 共通エラーハンドラー
 *
 * - システムエラーやログイン画面など、画面遷移を行うようなエラーはこの共通エラーハンドラーで処理する
 * - 502エラーのように、モーダルを表示するだけの場合はApp.vueのerrorCapturedで処理している
 *
 * @param error エラークラス
 * @param vm vueコンポーネント
 * @param info Vue 固有のエラー情報
 */
Vue.config.errorHandler = (error: any, vm: Vue, info: any) => {
  console.error("Vue.config.errorHandler:", error);
  const needLogout: boolean = errorHandle(error, vm, info);

  if (needLogout == true) {
    userStore.logout();
  }

  return;
};

/**
 * ### 共通エラーハンドラー実処理
 *
 * 共通エラーハンドラー以外でもエラーハンドラーと同一処理を行いたい場合があるためfunctionにしている
 * 例えば、Promise.all().catch()で例外処理をしたい場合は例外をthrowしても共通エラーハンドラーで処理できない
 * 上記の場合は本functionを実行することで共通エラーハンドラーの処理と同じ処理を行わせる
 *
 * - ステータスコード401ならログイン画面へ戻す
 * - ステータスコード403ならログイン画面へ戻す
 * - ステータスコード404ならNotFound画面へ戻す
 * - 上記以外はエラー情報を送信し、システムエラー画面へ遷移させる
 *
 * @param error エラークラス
 * @param vm vueコンポーネント
 * @param info Vue 固有のエラー情報
 *
 * @returns ログイン情報を破棄するか否か
 */
export function errorHandle(error: any, vm: Vue, info?: any): boolean {
  // developビルドの場合のみ、例外をログ出力する
  if (process.env.NODE_ENV == "development") {
    console.error(`Captured in errorHandle: ${info}`, error);
    console.debug(`before Url: ${commonStore.beforeUrl}`);
    console.debug(`current Url: ${vm.$route.fullPath}`);
  }

  // ネットワークのタイムアウトの場合、画面遷移はせずにalertだけ表示する
  // axiosのXMLHttp通信でブラウザから例外を受け取った場合、timeoutとabortはステータスコードにECONNABORTEDが格納される
  // ※axiosのxhr.jsを参照
  if (error.code == "ECONNABORTED") {
    // 複数回エラーハンドラーが呼び出された場合は何もしない
    if (commonStore.beforeUrl == vm.$route.fullPath) {
      return false;
    }

    try {
      const sendSystemErrorService: SendSystemErrorService = new SendSystemErrorService();
      sendSystemErrorService.post(error, userStore, vm.$router, info);
    } catch (error) {
      console.error(error);
    } finally {
      alert("ネットワーク接続がタイムアウトしました。\n通信環境をご確認のうえ、再度操作を行ってください。");
      commonStore.setBeforeUrl({ url: vm.$route.fullPath });
      commonStore.setBeforeLoginFacilityId({ id: userStore.facility.id });
    }
    return false;
  }

  // ネットワーク切断の場合、ネットワークエラー画面へ遷移する
  // axiosのXMLHttp通信でブラウザから例外を受け取った場合、timeoutとabort以外はステータスコードが「ERR_NETWORK」、メッセージに「Network Error」が格納される
  // 仕様変更でステータスコードやメッセージが変わる可能性を考慮しOR条件でチェックする
  // node_modules\axios\dist\axios.js、node_modules\axios\lib\core\AxiosError.jsあたりのコードで確認可能
  if (error.code == "ERR_NETWORK" || error.message == "Network Error") {
    // 複数回エラーハンドラーが呼び出された場合は何もしない
    if (commonStore.beforeUrl == vm.$route.fullPath) {
      return false;
    }

    try {
      const sendSystemErrorService: SendSystemErrorService = new SendSystemErrorService();
      sendSystemErrorService.post(error, userStore, vm.$router, info);
    } catch (error) {
      console.error(error);
    } finally {
      commonStore.setBeforeUrl({ url: vm.$route.fullPath });
      commonStore.setBeforeLoginFacilityId({ id: userStore.facility.id });
      vm.$router.push(Url.NetworkError);
    }
    return true;
  }

  // 非同期処理での例外の場合、エラーハンドラーは複数回呼び出される場合がある
  // 既にエラーハンドラーが呼び出されている場合は何もしない
  const current: string = vm.$route.fullPath;
  if (current == Url.Root || current == Url.NotFound || current == Url.SystemError || current == Url.Maintenance) {
    return false;
  }

  // エラーだが、ステータスコードに応じてエラーと扱わないものがある
  const systemError: SystemError = error;
  switch (systemError.statusCode) {
    // メンテナンス中はメンテナンス画面へ遷移させる
    case 503: {
      vm.$router.push(Url.Maintenance);
      return false;
    }
    // 認証失敗はログイン画面へ遷移させる
    case 401: {
      commonStore.setMessage({ message: "このアカウントは1時間以上操作されなかったため\n処理を中断しログアウトしました。\n再ログインしてください。" });
      commonStore.setBeforeUrl({ url: vm.$route.fullPath });
      commonStore.setBeforeLoginFacilityId({ id: userStore.facility.id });
      userStore.updateReservationsReminded(false);
      userStore.updateOutingsReminded(false);
      userStore.updateUnreadNotificationsReminded(false);
      vm.$router.push(Url.Root);
      return true;
    }
    // 認証有効期限切れの場合はログイン画面へ遷移させる
    case 403: {
      commonStore.setMessage({ message: "認証情報の期限が切れました。再度ログインしてください。" });
      commonStore.setBeforeUrl({ url: vm.$route.fullPath });
      commonStore.setBeforeLoginFacilityId({ id: userStore.facility.id });
      userStore.updateReservationsReminded(false);
      userStore.updateOutingsReminded(false);
      userStore.updateUnreadNotificationsReminded(false);
      vm.$router.push(Url.Root);
      return true;
    }
    // 存在しない情報へのアクセスは404 NotFound画面へ遷移させる
    case 404: {
      vm.$router.push(Url.NotFound);
      return false;
    }
    // 許可されていない機能へのアクセスは405が返却されるが、404 NotFound画面へ遷移させる
    case 405: {
      vm.$router.push(Url.NotFound);
      return false;
    }
  }

  console.log("503、401、403、404、405ではない");

  // 上記以外のステータスコードの場合はエラー内容をAPI経由でサーバーへ送信する
  // 複数回エラーハンドラーが呼ばれた場合、facilityId=0になっているのでその場合は何もしない
  if (0 < userStore.loginUser.facilityId) {
    const sendSystemErrorService: SendSystemErrorService = new SendSystemErrorService();
    sendSystemErrorService.post(error, userStore, vm.$router, info);
  }

  switch (systemError.statusCode) {
    // バグだと判断されるもの
    case 400:
    case 409:
    case 413:
    case 500: {
      // cognito認証でタイムアウトする現象が発生している。本来なら504が来るべきだが500が来るのでメッセージで判断する
      if (systemError.originalResponse.message && systemError.originalResponse.message.indexOf("Endpoint request timed out") != -1) {
        alert("サーバーが応答しません。\n時間を空けてもう一度読み込んでください。");
        return false;
      }

      vm.$router.push(Url.SystemError);
      return true;
    }
    // その他
    default: {
      // システムエラー
      if (systemError.hitomeqErrorType == HitomeqErrorType.SYSTEM_ERROR) {
        vm.$router.push(Url.SystemError);
        return true;
      }
      // GETのリトライ要求
      else if (systemError.hitomeqErrorType == HitomeqErrorType.REQUEST_RETRY && systemError.method == "get") {
        alert("サーバーが応答しません。\n時間を空けてもう一度読み込んでください。");
        return false;
      }
      // GET以外のリトライ要求
      else if (systemError.hitomeqErrorType == HitomeqErrorType.REQUEST_RETRY && systemError.method != "get") {
        alert("サーバーが応答しません。\n時間を空けてもう一度実行してください。");
        return false;
      }
      // 無視する
      else if (systemError.hitomeqErrorType == HitomeqErrorType.IGNORE) {
        return false;
      }
    }
  }

  return false;
}
